import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Using Material-UI icons instead of lucide-react
import {
    Security as Shield,
    BarChart as BarChart3,
    FlashOn as Zap,
    TrendingUp,
    Group as Users,
    Star,
    ChevronLeft,
    ChevronRight,
    ArrowForward as ArrowRight,
    Add as Plus,
    Close as X,
    CheckCircle,
    Schedule as Clock,
    EmojiEvents as Award,
    Public as Globe
} from '@mui/icons-material';

// Import the reviews service
import reviewsService from '../../services/reviewsService';

const LandingPage = () => {
    const [currentPage, setCurrentPage] = useState('landing');
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const [reviewDialog, setReviewDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [newReview, setNewReview] = useState({
        name: '',
        company: '',
        rating: 5,
        comment: ''
    });
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({
        total_reviews: 0,
        average_rating: 4.8,
        rating_distribution: {}
    });
    const navigate = useNavigate();

    const handleNavigation = (path) => {
        navigate(path);
    };

    const handleSocialLink = (platform) => {
        const urls = {
            linkedin: 'https://linkedin.com/company/riskas',
            twitter: 'https://twitter.com/riskas',
            facebook: 'https://facebook.com/riskas'
        };
        window.open(urls[platform], '_blank');
    };

    const features = [
        {
            icon: <BarChart3 style={{ fontSize: 48, color: '#2563eb' }} />,
            title: "AI-Powered Analysis",
            description: "Advanced artificial intelligence analyzes complex risk patterns, identifies hidden correlations, and provides actionable insights for better decision making across all business domains.",
            badge: "Most Popular"
        },
        {
            icon: <Zap style={{ fontSize: 48, color: '#eab308' }} />,
            title: "Real-time Monitoring",
            description: "Continuous monitoring of risk factors with instant alerts and notifications when critical thresholds are reached. Get notified before risks become problems.",
            badge: "New"
        },
        {
            icon: <Shield style={{ fontSize: 48, color: '#16a34a' }} />,
            title: "Enterprise Security",
            description: "Bank-grade security with end-to-end encryption, secure authentication, compliance with SOC2, GDPR, and industry standards. Your data is always protected.",
            badge: "Certified"
        },
        {
            icon: <TrendingUp style={{ fontSize: 48, color: '#9333ea' }} />,
            title: "Predictive Analytics",
            description: "Leverage historical data and machine learning models to predict potential risks up to 12 months in advance and recommend preventive measures.",
            badge: "AI-Driven"
        },
        {
            icon: <Globe style={{ fontSize: 48, color: '#4f46e5' }} />,
            title: "Detailed Reporting",
            description: "Generate comprehensive reports with interactive visualizations, trend analysis, and exportable formats for stakeholder presentations and regulatory compliance.",
            badge: "Customizable"
        },
        {
            icon: <Users style={{ fontSize: 48, color: '#db2777' }} />,
            title: "Team Collaboration",
            description: "Multi-user support with granular role-based access controls, allowing teams to collaborate effectively on risk assessments with full audit trails.",
            badge: "Collaborative"
        }
    ];

    const stats = [
        { number: "15,000+", label: "Risk Assessments Completed", icon: <Award style={{ fontSize: 32 }} /> },
        { number: "750+", label: "Enterprise Clients", icon: <Users style={{ fontSize: 32 }} /> },
        { number: "99.99%", label: "System Uptime", icon: <Clock style={{ fontSize: 32 }} /> },
        { number: "$50M+", label: "Risk Losses Prevented", icon: <Shield style={{ fontSize: 32 }} /> }
    ];

    const benefits = [
        "Reduce risk assessment time by 80%",
        "Prevent losses with predictive insights",
        "Ensure regulatory compliance",
        "Scale across global operations",
        "Integrate with existing systems",
        "24/7 expert support included"
    ];

    // Load reviews and stats from backend
    useEffect(() => {
        loadReviews();
        loadReviewStats();
    }, []);

    const loadReviews = async () => {
        try {
            setLoading(true);
            const reviewsData = await reviewsService.getReviews({
                limit: 50,
                featuredOnly: false,
                minRating: 1
            });
            setReviews(reviewsData);
        } catch (error) {
            console.error('Error loading reviews:', error);
            // Fallback to default reviews if API fails
            setReviews([
                {
                    id: 1,
                    name: "Sarah Johnson",
                    company: "TechCorp Inc.",
                    role: "Risk Manager",
                    rating: 5,
                    comment: "RiskAS has completely revolutionized our approach to risk assessment. The AI-powered insights are incredibly accurate and have helped us identify and prevent several potential issues before they became major problems.",
                    created_at: "2024-12-15T00:00:00Z"
                },
                {
                    id: 2,
                    name: "Michael Chen",
                    company: "Financial Solutions Ltd",
                    role: "Chief Risk Officer",
                    rating: 5,
                    comment: "Outstanding platform! The comprehensive analysis capabilities and intuitive interface make complex risk assessments feel manageable. Our team productivity has increased by 40% since implementation.",
                    created_at: "2024-12-10T00:00:00Z"
                },
                {
                    id: 3,
                    name: "Emily Rodriguez",
                    company: "Healthcare Dynamics",
                    role: "Compliance Director",
                    rating: 4,
                    comment: "Excellent tool for healthcare risk management. The detailed reports and real-time monitoring capabilities have significantly improved our compliance processes.",
                    created_at: "2024-12-08T00:00:00Z"
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const loadReviewStats = async () => {
        try {
            const stats = await reviewsService.getReviewStats();
            setReviewStats(stats);
        } catch (error) {
            console.error('Error loading review stats:', error);
            // Use fallback stats
            setReviewStats({
                total_reviews: reviews.length || 3,
                average_rating: 4.8,
                rating_distribution: { 1: 0, 2: 0, 3: 1, 4: 5, 5: 12 }
            });
        }
    };

    // Set current page based on URL path simulation
    useEffect(() => {
        const path = window.location.hash || '#landing';
        if (path === '#reviews') {
            setCurrentPage('reviews');
        } else {
            setCurrentPage('landing');
        }
    }, []);

    useEffect(() => {
        if (reviews.length > 0) {
            const timer = setInterval(() => {
                setCurrentTestimonial((prev) => (prev + 1) % reviews.length);
            }, 6000);
            return () => clearInterval(timer);
        }
    }, [reviews.length]);

    const nextTestimonial = () => {
        setCurrentTestimonial((prev) => (prev + 1) % reviews.length);
    };

    const prevTestimonial = () => {
        setCurrentTestimonial((prev) => (prev - 1 + reviews.length) % reviews.length);
    };

    const handleAddReview = async () => {
        if (!newReview.name.trim() || !newReview.comment.trim()) {
            setNotification({
                show: true,
                message: 'Please fill in all required fields',
                type: 'error'
            });
            setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
            return;
        }

        if (newReview.comment.length < 10) {
            setNotification({
                show: true,
                message: 'Review must be at least 10 characters long',
                type: 'error'
            });
            setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
            return;
        }

        try {
            setLoading(true);

            // Submit to backend
            const reviewData = {
                name: newReview.name.trim(),
                company: newReview.company.trim() || null,
                rating: newReview.rating,
                comment: newReview.comment.trim()
            };

            const createdReview = await reviewsService.createReview(reviewData);

            // Add to local state
            setReviews([createdReview, ...reviews]);

            // Reset form
            setNewReview({ name: '', company: '', rating: 5, comment: '' });
            setReviewDialog(false);

            // Refresh stats
            loadReviewStats();

            setNotification({
                show: true,
                message: 'Thank you for your review! It has been submitted successfully.',
                type: 'success'
            });
            setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
        } catch (error) {
            console.error('Error submitting review:', error);
            setNotification({
                show: true,
                message: 'Failed to submit review. Please try again.',
                type: 'error'
            });
            setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
        } finally {
            setLoading(false);
        }
    };

    const StarRating = ({ rating, onRatingChange, readOnly = false, size = 20 }) => {
        return (
            <div style={{ display: 'flex', gap: '4px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        style={{
                            fontSize: size,
                            color: star <= rating ? '#fbbf24' : '#d1d5db',
                            cursor: !readOnly ? 'pointer' : 'default'
                        }}
                        onClick={() => !readOnly && onRatingChange && onRatingChange(star)}
                    />
                ))}
            </div>
        );
    };

    const getAvatarLetter = (name) => {
        if (!name) return 'U';
        return name.charAt(0).toUpperCase();
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return 'Recent';
        }
    };

    const Header = () => (
        <header style={{
            position: 'fixed',
            top: 0,
            width: '100%',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid #e5e7eb',
            zIndex: 50,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '64px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ background: '#2563eb', padding: '8px', borderRadius: '8px' }}>
                            <Shield style={{ fontSize: 24, color: 'white' }} />
                        </div>
                        <span style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>RiskAS</span>
                    </div>
                    <nav style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                        <button
                            onClick={() => { setCurrentPage('landing'); window.location.hash = 'landing'; }}
                            style={{
                                fontWeight: '500',
                                color: currentPage === 'landing' ? '#2563eb' : '#6b7280',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            Home
                        </button>
                        <button
                            onClick={() => { setCurrentPage('reviews'); window.location.hash = 'reviews'; }}
                            style={{
                                fontWeight: '500',
                                color: currentPage === 'reviews' ? '#2563eb' : '#6b7280',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            Reviews
                        </button>
                        <button
                            onClick={() => window.location.href = '/login'}
                            style={{
                                border: '2px solid #2563eb',
                                color: '#2563eb',
                                background: 'white',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            Login
                        </button>
                        <button
                            onClick={() => window.location.href = '/register'}
                            style={{
                                background: '#2563eb',
                                color: 'white',
                                padding: '10px 24px',
                                borderRadius: '8px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            Register
                        </button>
                    </nav>
                </div>
            </div>
        </header>
    );

    const LandingContent = () => (
        <div>
            {/* Hero Section */}
            <section style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #eff6ff 100%)',
                paddingTop: '96px',
                paddingBottom: '80px'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <span style={{
                            background: '#dbeafe',
                            color: '#1e40af',
                            padding: '8px 16px',
                            borderRadius: '50px',
                            fontSize: '14px',
                            fontWeight: '600'
                        }}>
                            🚀 Trusted by 750+ Enterprise Clients
                        </span>
                    </div>
                    <h1 style={{
                        fontSize: '64px',
                        fontWeight: 'bold',
                        marginBottom: '32px',
                        lineHeight: '1.1',
                        color: '#111827'
                    }}>
                        Advanced Risk Assessment<br />
                        <span style={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Powered by AI
                        </span>
                    </h1>
                    <p style={{
                        fontSize: '24px',
                        color: '#6b7280',
                        marginBottom: '40px',
                        maxWidth: '800px',
                        margin: '0 auto 40px auto',
                        lineHeight: '1.5'
                    }}>
                        Transform your risk management with intelligent analysis, real-time monitoring,
                        and actionable insights that protect your business and drive growth.
                    </p>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: '48px' }}>
                        <button
                            onClick={() => window.location.href = '/register'}
                            style={{
                                background: '#2563eb',
                                color: 'white',
                                padding: '16px 32px',
                                borderRadius: '8px',
                                fontSize: '18px',
                                fontWeight: '600',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)'
                            }}
                        >
                            Get Started
                            <ArrowRight style={{ fontSize: 20 }} />
                        </button>

                    </div>

                    {/* Trust Indicators */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', color: '#6b7280', fontSize: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle style={{ fontSize: 16, color: '#10b981' }} />
                            <span>Free</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle style={{ fontSize: 16, color: '#10b981' }} />
                            <span>No credit card required</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle style={{ fontSize: 16, color: '#10b981' }} />
                            <span>Setup in under 5 minutes</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section style={{ padding: '64px 0', background: 'white', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '32px' }}>
                        {stats.map((stat, index) => (
                            <div key={index} style={{ textAlign: 'center' }}>
                                <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '64px',
                                    height: '64px',
                                    background: '#dbeafe',
                                    borderRadius: '50%',
                                    marginBottom: '16px',
                                    color: '#2563eb'
                                }}>
                                    {stat.icon}
                                </div>
                                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
                                    {stat.number}
                                </div>
                                <div style={{ color: '#6b7280', fontWeight: '500' }}>{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section style={{ padding: '80px 0', background: '#f9fafb' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <h2 style={{ fontSize: '48px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>
                            Powerful Features That Drive Results
                        </h2>
                        <p style={{ fontSize: '20px', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
                            Everything you need to assess, monitor, and mitigate risks across your entire organization
                        </p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' }}>
                        {features.map((feature, index) => (
                            <div key={index} style={{
                                background: 'white',
                                padding: '32px',
                                borderRadius: '16px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                                border: '1px solid #f3f4f6',
                                position: 'relative'
                            }}>
                                {feature.badge && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-12px',
                                        left: '24px',
                                        background: '#2563eb',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: '600'
                                    }}>
                                        {feature.badge}
                                    </div>
                                )}
                                <div style={{ marginBottom: '24px' }}>
                                    {feature.icon}
                                </div>
                                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
                                    {feature.title}
                                </h3>
                                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section style={{ padding: '64px 0', background: 'white' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'center' }}>
                        <div>
                            <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>
                                Why Leading Companies Choose RiskAS
                            </h2>
                            <p style={{ fontSize: '18px', color: '#6b7280', marginBottom: '32px' }}>
                                Join the world's most innovative companies who trust RiskAS to protect their future and drive sustainable growth.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {benefits.map((benefit, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{
                                            width: '24px',
                                            height: '24px',
                                            background: '#dcfce7',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <CheckCircle style={{ fontSize: 16, color: '#16a34a' }} />
                                        </div>
                                        <span style={{ color: '#374151', fontWeight: '500' }}>{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ position: 'relative' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
                                borderRadius: '16px',
                                padding: '32px',
                                transform: 'rotate(3deg)',
                                boxShadow: '0 20px 25px rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{
                                    background: 'white',
                                    borderRadius: '12px',
                                    padding: '24px',
                                    transform: 'rotate(-3deg)'
                                }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#2563eb', marginBottom: '8px' }}>
                                            40%
                                        </div>
                                        <div style={{ color: '#6b7280' }}>Average Risk Reduction</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Slider */}
            {reviews.length > 0 && (
                <section style={{ padding: '80px 0', background: '#f9fafb' }}>
                    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                            <h2 style={{ fontSize: '48px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>
                                What Our Customers Say
                            </h2>
                            <p style={{ fontSize: '20px', color: '#6b7280' }}>
                                Join thousands of satisfied customers who trust RiskAS to protect their business
                            </p>
                        </div>

                        <div style={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
                            <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                boxShadow: '0 20px 25px rgba(0, 0, 0, 0.1)',
                                padding: '48px',
                                textAlign: 'center',
                                minHeight: '400px'
                            }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '32px',
                                    fontWeight: 'bold',
                                    margin: '0 auto 24px auto'
                                }}>
                                    {getAvatarLetter(reviews[currentTestimonial]?.name)}
                                </div>
                                <div style={{ marginBottom: '24px' }}>
                                    <StarRating rating={reviews[currentTestimonial]?.rating} readOnly size={24} />
                                </div>
                                <blockquote style={{
                                    fontSize: '20px',
                                    color: '#374151',
                                    marginBottom: '32px',
                                    fontStyle: 'italic',
                                    lineHeight: '1.6'
                                }}>
                                    "{reviews[currentTestimonial]?.comment}"
                                </blockquote>
                                <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '24px' }}>
                                    <div style={{ fontWeight: 'bold', color: '#111827', fontSize: '18px', marginBottom: '4px' }}>
                                        {reviews[currentTestimonial]?.name}
                                    </div>
                                    <div style={{ color: '#6b7280' }}>
                                        {reviews[currentTestimonial]?.role} at {reviews[currentTestimonial]?.company}
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={prevTestimonial}
                                style={{
                                    position: 'absolute',
                                    left: '-64px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'white',
                                    border: '1px solid #e5e7eb',
                                    padding: '16px',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <ChevronLeft style={{ fontSize: 24 }} />
                            </button>
                            <button
                                onClick={nextTestimonial}
                                style={{
                                    position: 'absolute',
                                    right: '-64px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'white',
                                    border: '1px solid #e5e7eb',
                                    padding: '16px',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <ChevronRight style={{ fontSize: 24 }} />
                            </button>
                        </div>

                        <div style={{ textAlign: 'center', marginTop: '48px' }}>
                            <button
                                onClick={() => { setCurrentPage('reviews'); window.location.hash = 'reviews'; }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    border: '2px solid #2563eb',
                                    color: '#2563eb',
                                    background: 'white',
                                    padding: '12px 32px',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    gap: '8px',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                View All Reviews
                                <ArrowRight style={{ fontSize: 16 }} />
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {/* CTA Section */}
            <section style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                color: 'white',
                padding: '80px 0'
            }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px', textAlign: 'center' }}>
                    <h2 style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '32px' }}>
                        Ready to Transform Your Risk Management?
                    </h2>
                    <p style={{ fontSize: '20px', marginBottom: '40px', color: 'rgba(255, 255, 255, 0.9)' }}>
                        Join thousands of businesses that trust RiskAS to protect their future.
                        Start today and see the difference AI-powered risk assessment can make.
                    </p>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                        <button
                            onClick={() => window.location.href = '/register'}
                            style={{
                                background: 'white',
                                color: '#2563eb',
                                padding: '16px 32px',
                                borderRadius: '8px',
                                fontSize: '18px',
                                fontWeight: '600',
                                border: 'none',
                                cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            Get Started Now
                        </button>

                    </div>
                </div>
            </section>
        </div>
    );

    const ReviewsContent = () => (
        <div style={{ paddingTop: '96px', paddingBottom: '80px', background: '#f9fafb', minHeight: '100vh' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '48px',
                    gap: '24px'
                }}>
                    <div>
                        <h1 style={{ fontSize: '48px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
                            Customer Reviews
                        </h1>
                        <p style={{ fontSize: '20px', color: '#6b7280' }}>
                            See what our customers are saying about RiskAS and their success stories
                        </p>
                    </div>
                    <button
                        onClick={() => setReviewDialog(true)}
                        style={{
                            background: '#2563eb',
                            color: 'white',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            fontWeight: '600',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)'
                        }}
                    >
                        <Plus style={{ fontSize: 20 }} />
                        Add Your Review
                    </button>
                </div>

                {/* Reviews Stats */}
                <div style={{
                    background: 'white',
                    padding: '32px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #f3f4f6',
                    marginBottom: '48px'
                }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', alignItems: 'center' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '72px', fontWeight: 'bold', color: '#2563eb', marginBottom: '16px' }}>
                                {reviewStats.average_rating}
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <StarRating rating={Math.round(reviewStats.average_rating)} readOnly size={32} />
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '18px' }}>
                                Average rating from <strong>{reviewStats.total_reviews} verified reviews</strong>
                            </div>
                        </div>
                        <div>
                            <h3 style={{ fontWeight: '600', color: '#111827', marginBottom: '16px', fontSize: '18px' }}>
                                Most mentioned benefits:
                            </h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '8px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>
                                    Excellent Service
                                </span>
                                <span style={{ background: '#dcfce7', color: '#166534', padding: '8px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>
                                    Easy to Use
                                </span>
                                <span style={{ background: '#f3e8ff', color: '#7c2d12', padding: '8px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>
                                    AI-Powered
                                </span>
                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '8px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>
                                    Great ROI
                                </span>
                                <span style={{ background: '#fce7f3', color: '#be185d', padding: '8px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>
                                    24/7 Support
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{ color: '#6b7280', fontSize: '18px' }}>Loading reviews...</div>
                    </div>
                )}

                {/* Reviews List */}
                {!loading && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '32px' }}>
                        {reviews.map((review) => (
                            <div key={review.id} style={{
                                background: 'white',
                                padding: '32px',
                                borderRadius: '16px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
                                border: '1px solid #f3f4f6'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                                    <div style={{
                                        width: '56px',
                                        height: '56px',
                                        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '18px',
                                        marginRight: '16px'
                                    }}>
                                        {getAvatarLetter(review.name)}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 'bold', color: '#111827', fontSize: '18px' }}>
                                            {review.name}
                                        </div>
                                        <div style={{ color: '#6b7280' }}>
                                            {review.role || 'Verified User'} at <span style={{ fontWeight: '600' }}>{review.company || 'Company'}</span>
                                        </div>
                                    </div>
                                    <div style={{ color: '#9ca3af', fontSize: '14px' }}>
                                        {formatDate(review.created_at)}
                                    </div>
                                </div>
                                <div style={{ marginBottom: '16px' }}>
                                    <StarRating rating={review.rating} readOnly />
                                </div>
                                <p style={{ color: '#374151', lineHeight: '1.6' }}>{review.comment}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Load More Button */}
                {!loading && reviews.length > 0 && (
                    <div style={{ textAlign: 'center', marginTop: '48px' }}>
                        <button style={{
                            border: '2px solid #d1d5db',
                            color: '#374151',
                            background: 'white',
                            padding: '12px 32px',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '16px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                        }}>
                            Load More Reviews
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
            <Header />

            {currentPage === 'landing' && <LandingContent />}
            {currentPage === 'reviews' && <ReviewsContent />}

            {/* Add Review Modal */}
            {reviewDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 50,
                    padding: '16px',
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        maxWidth: '500px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
                    }}>
                        <div style={{ padding: '32px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                                    Share Your Experience
                                </h3>
                                <button
                                    onClick={() => setReviewDialog(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#9ca3af',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <X style={{ fontSize: 24 }} />
                                </button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Your Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={newReview.name}
                                        onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '16px',
                                            outline: 'none',
                                            transition: 'border-color 0.2s'
                                        }}
                                        placeholder="Enter your full name"
                                        maxLength="100"
                                        onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Company (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={newReview.company}
                                        onChange={(e) => setNewReview({ ...newReview, company: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '16px',
                                            outline: 'none',
                                            transition: 'border-color 0.2s'
                                        }}
                                        placeholder="Enter your company name"
                                        maxLength="100"
                                        onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Rating *
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <StarRating
                                            rating={newReview.rating}
                                            onRatingChange={(rating) => setNewReview({ ...newReview, rating })}
                                            size={32}
                                        />
                                        <span style={{ fontSize: '14px', color: '#6b7280' }}>
                                            ({newReview.rating} star{newReview.rating !== 1 ? 's' : ''})
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Your Review *
                                    </label>
                                    <textarea
                                        value={newReview.comment}
                                        onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                                        rows={5}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '16px',
                                            outline: 'none',
                                            transition: 'border-color 0.2s',
                                            resize: 'none',
                                            fontFamily: 'inherit'
                                        }}
                                        placeholder="Share your experience with RiskAS... What did you like most? How did it help your business?"
                                        maxLength="1000"
                                        onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                    />
                                    <div style={{ textAlign: 'right', fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                                        {newReview.comment.length}/1000 characters
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '16px', marginTop: '32px' }}>
                                <button
                                    onClick={() => setReviewDialog(false)}
                                    disabled={loading}
                                    style={{
                                        flex: 1,
                                        padding: '12px 24px',
                                        border: '2px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        color: '#374151',
                                        background: 'white',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        opacity: loading ? 0.5 : 1
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddReview}
                                    disabled={!newReview.name.trim() || !newReview.comment.trim() || loading}
                                    style={{
                                        flex: 1,
                                        padding: '12px 24px',
                                        background: '#2563eb',
                                        color: 'white',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        border: 'none',
                                        cursor: (!newReview.name.trim() || !newReview.comment.trim() || loading) ? 'not-allowed' : 'pointer',
                                        opacity: (!newReview.name.trim() || !newReview.comment.trim() || loading) ? 0.5 : 1
                                    }}
                                >
                                    {loading ? 'Submitting...' : 'Submit Review'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Toast */}
            {notification.show && (
                <div style={{
                    position: 'fixed',
                    top: '24px',
                    right: '24px',
                    zIndex: 50
                }}>
                    <div style={{
                        padding: '16px',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
                        maxWidth: '400px',
                        background: notification.type === 'success' ? '#10b981' : '#ef4444',
                        color: 'white'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {notification.type === 'success' ? (
                                    <CheckCircle style={{ fontSize: 20, marginRight: '12px' }} />
                                ) : (
                                    <X style={{ fontSize: 20, marginRight: '12px' }} />
                                )}
                                <span style={{ fontWeight: '500' }}>{notification.message}</span>
                            </div>
                            <button
                                onClick={() => setNotification({ ...notification, show: false })}
                                style={{
                                    marginLeft: '16px',
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                <X style={{ fontSize: 16 }} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <footer style={{ background: '#111827', color: 'white', padding: '64px 0' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '32px', marginBottom: '32px' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                                <div style={{ background: '#2563eb', padding: '8px', borderRadius: '8px' }}>
                                    <Shield style={{ fontSize: 32, color: 'white' }} />
                                </div>
                                <span style={{ fontSize: '32px', fontWeight: 'bold' }}>RiskAS</span>
                            </div>
                            <p style={{ color: '#d1d5db', maxWidth: '400px', fontSize: '18px', lineHeight: '1.6', marginBottom: '24px' }}>
                                Leading the future of risk assessment with AI-powered insights
                                and comprehensive analysis tools for modern businesses worldwide.
                            </p>
                            <div style={{ display: 'flex', gap: '16px' }}>
                                <div
                                    onClick={() => handleSocialLink('linkedin')}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        background: '#374151',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#0077b5'}
                                    onMouseLeave={(e) => e.target.style.background = '#374151'}
                                >
                                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Li</span>
                                </div>
                                <div
                                    onClick={() => handleSocialLink('twitter')}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        background: '#374151',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#1da1f2'}
                                    onMouseLeave={(e) => e.target.style.background = '#374151'}
                                >
                                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Tw</span>
                                </div>
                                <div
                                    onClick={() => handleSocialLink('facebook')}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        background: '#374151',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#1877f2'}
                                    onMouseLeave={(e) => e.target.style.background = '#374151'}
                                >
                                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Fb</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 style={{ fontWeight: 'bold', marginBottom: '24px', fontSize: '18px' }}>Product</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', color: '#d1d5db' }}>
                                <span
                                    onClick={() => handleNavigation('/')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'white'}
                                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                                >
                                    Features
                                </span>
                                <span
                                    onClick={() => handleNavigation('/reviews')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'white'}
                                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                                >
                                    Reviews
                                </span>
                                <span
                                    onClick={() => handleNavigation('/register')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'white'}
                                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                                >
                                    Get Started
                                </span>
                                <span
                                    onClick={() => window.open('https://docs.riskas.com', '_blank')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'white'}
                                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                                >
                                    Documentation
                                </span>
                            </div>
                        </div>

                        <div>
                            <h4 style={{ fontWeight: 'bold', marginBottom: '24px', fontSize: '18px' }}>Company</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', color: '#d1d5db' }}>
                                <span
                                    onClick={() => handleNavigation('/about')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'white'}
                                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                                >
                                    About Us
                                </span>
                                <span
                                    onClick={() => handleNavigation('/contact')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'white'}
                                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                                >
                                    Contact
                                </span>
                                <span
                                    onClick={() => window.open('https://careers.riskas.com', '_blank')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'white'}
                                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                                >
                                    Careers
                                </span>
                                <span
                                    onClick={() => window.open('https://blog.riskas.com', '_blank')}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = 'white'}
                                    onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                                >
                                    Blog
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        borderTop: '1px solid #374151',
                        paddingTop: '32px',
                        display: 'flex',
                        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: window.innerWidth <= 768 ? 'center' : 'center',
                        gap: window.innerWidth <= 768 ? '16px' : '0'
                    }}>
                        <p style={{ color: '#9ca3af', margin: 0 }}>
                            © Akinwale Akinniranye {new Date().getFullYear()}. All rights reserved.
                        </p>
                        <div style={{ display: 'flex', gap: '24px', color: '#9ca3af', fontSize: '14px' }}>
                            <span
                                onClick={() => handleNavigation('/privacy-policy')}
                                style={{
                                    cursor: 'pointer',
                                    transition: 'color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.color = 'white'}
                                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                            >
                                Privacy Policy
                            </span>
                            <span
                                onClick={() => handleNavigation('/terms')}
                                style={{
                                    cursor: 'pointer',
                                    transition: 'color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.color = 'white'}
                                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                            >
                                Terms of Service
                            </span>
                            <span
                                onClick={() => handleNavigation('/cookie-policy')}
                                style={{
                                    cursor: 'pointer',
                                    transition: 'color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.color = 'white'}
                                onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                            >
                                Cookie Policy
                            </span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;